/**
 * direct-stream.js — raw byte-range access to one library file via zapd's
 * stream session (`POST /api-zapd/stream/open` → `GET /api-zapd/stream/<sid>`).
 *
 * Replaces the decommissioned `/media/<path>` route (zapd answers it with
 * MEDIA_DECOMMISSIONED, HttpServer.cpp) for every web caller that needs the
 * original container: the music deck (decodeAudioData wants the whole file),
 * the VLC/mpv link in the files view. Video playback itself keeps its own
 * session handling in player/backend.js (streamOpen/streamClose) — this module
 * is the thin variant for callers outside the player: no seek index, no
 * buffering callback, no kind inference.
 *
 * Auth: the bearer from core/auth.js goes into the Authorization header, never
 * into the URL — the SPA holds the access token in memory only. `withToken()`
 * exists for the one context that cannot send a header (a URL handed to an
 * external player); that copy is short-lived by design (idle reaper ~5 min).
 */

import { auth } from './auth.js';

const ZAPD_BASE = '/api-zapd';

/** @returns {Record<string,string>} */
export function bearerHeaders() {
  const tok = auth.getToken();
  return tok ? { Authorization: 'Bearer ' + tok } : {};
}

/**
 * Open a stream session for an absolute library path.
 * Mirrors backend.js streamOpen's 503 retry (the server may still be priming
 * its seek index), bounded by `bufferTimeoutMs`.
 * @param {string} path absolute filesystem path (media_items.path)
 * @param {{signal?: AbortSignal, bufferTimeoutMs?: number}} [opts]
 * @returns {Promise<{sessionId: string, streamUrl: string, fileSize: number}>}
 */
export async function openDirectStream(path, opts = {}) {
  if (!path || path[0] !== '/') throw new Error('direct-stream: absolute path required');
  const deadline = Date.now() + (opts.bufferTimeoutMs || 60000);
  for (;;) {
    const r = await fetch(ZAPD_BASE + '/stream/open', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...bearerHeaders() },
      body: JSON.stringify({ path }),
      signal: opts.signal,
    });
    if (r.ok) {
      const data = await r.json();
      return {
        sessionId: data.session_id,
        streamUrl: data.stream_url, // relative (/api-zapd/stream/<sid>), token-free
        fileSize: Number(data.file_size) || 0,
      };
    }
    if (r.status === 503 && Date.now() < deadline) {
      await r.text().catch(() => '');
      const ra = parseInt(r.headers.get('Retry-After') || '', 10);
      const retrySec = !Number.isNaN(ra) && ra > 0 ? Math.min(ra, 5) : 2;
      await new Promise((res, rej) => {
        const t = setTimeout(res, retrySec * 1000);
        opts.signal?.addEventListener(
          'abort',
          () => {
            clearTimeout(t);
            rej(new DOMException('aborted', 'AbortError'));
          },
          { once: true },
        );
      });
      continue;
    }
    const txt = await r.text().catch(() => '');
    throw new Error(`zapd stream/open ${r.status}: ${txt || r.statusText}`);
  }
}

/** Release a session (best-effort; the idle reaper covers a lost request). */
export async function closeDirectStream(sessionId) {
  if (!sessionId) return;
  try {
    await fetch(ZAPD_BASE + '/stream/' + encodeURIComponent(sessionId), {
      method: 'DELETE',
      credentials: 'include',
      headers: bearerHeaders(),
    });
  } catch {
    /* ignore */
  }
}

/**
 * Fetch the whole file of a session as bytes (music deck: decodeAudioData
 * needs the complete container; a range window brings nothing here).
 * @param {string} streamUrl
 * @param {{signal?: AbortSignal}} [opts]
 * @returns {Promise<ArrayBuffer>}
 */
export async function fetchStreamBytes(streamUrl, opts = {}) {
  const r = await fetch(streamUrl, {
    credentials: 'include',
    headers: bearerHeaders(),
    signal: opts.signal,
  });
  if (!r.ok) throw new Error(`zapd stream ${r.status}`);
  return r.arrayBuffer();
}

/**
 * Absolute URL with the access token as query — ONLY for a copy handed to an
 * external player / the clipboard, which cannot send headers. Never use it
 * for the SPA's own fetches, never persist it: the token is the user's
 * in-memory session and the URL dies with the session's idle reaper anyway.
 * @param {string} url relative or absolute stream URL
 */
export function withToken(url) {
  if (!url) return '';
  const abs = url.startsWith('http') ? url : window.location.origin + url;
  const tok = auth.getToken();
  if (!tok) return abs;
  return abs + (abs.includes('?') ? '&' : '?') + 'token=' + encodeURIComponent(tok);
}
